import axios from 'axios';
import moment from 'moment';
import classNames from 'classnames';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Loader from 'components/Base/Loader';

const AthenaWelcome = () => {
    const navigate = useNavigate();
    const [startDate] = useState(moment().format('YYYY-MM-DD'));
    const [endDate] = useState(moment().add(7, 'days').format('YYYY-MM-DD'));
    const [loading, setLoading] = useState(false);
    const [departments, setDepartments] = useState(null);
    const [slots, setSlots] = useState([]);
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [error, setError] = useState('');
    const [hasFetched, setHasFetched] = useState(false);

    useEffect(() => {
        let cancelled = false;
        axios
            .get('/athena/departments')
            .then(({ data }) => {
                if (!cancelled) setDepartments(Array.isArray(data) ? data : []);
            })
            .catch((err) => {
                console.error('Departments load error', err);
                if (!cancelled) {
                    setDepartments([]);
                    setError('Error loading departments. Please try again later.');
                }
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const fetchSlots = useCallback(async () => {
        if (!departments || departments.length === 0) return;
        if (!startDate || !endDate) return;

        setLoading(true);
        setError('');

        try {
            const slotChunks = await Promise.all(
                departments.map(async (department) => {
                    try {
                        const res = await axios.post(
                            '/athena/appointment/open',
                            { startDate, endDate, departmentId: department.id, reasonId: -1 },
                            { timeout: 120000 }
                        );
                        return Array.isArray(res.data) ? res.data : [];
                    } catch (deptErr) {
                        console.warn('Department open slots error', department.id, deptErr);
                        return [];
                    }
                })
            );

            const all = slotChunks.flat();

            const unique = Object.values(
                all.reduce((acc, slot) => {
                    const key = [
                        slot.date,
                        slot.starttime,
                        slot.duration,
                        slot.departmentid ?? '',
                        slot.appointmentid ?? slot.id ?? '',
                    ].join('|');
                    if (!acc[key]) acc[key] = slot;
                    return acc;
                }, {})
            );

            unique.sort((a, b) => {
                const da = moment(a.date, ['YYYY-MM-DD', 'MM/DD/YYYY']);
                const db = moment(b.date, ['YYYY-MM-DD', 'MM/DD/YYYY']);
                if (da.isSame(db)) {
                    const ta = moment(a.starttime, ['HH:mm', 'hh:mm A']);
                    const tb = moment(b.starttime, ['HH:mm', 'hh:mm A']);
                    return ta.diff(tb);
                }
                return da.diff(db);
            });

            setSlots(unique);
        } catch (err) {
            console.error('Open slots load error', err);
            setError('Failed to load available slots. Please try again later.');
            setSlots([]);
        } finally {
            setLoading(false);
            setHasFetched(true);
        }
    }, [departments, startDate, endDate]);

    useEffect(() => {
        fetchSlots();
    }, [fetchSlots]);

    const groupedSlots = useMemo(() => {
        return slots.reduce((acc, slot) => {
            const dateKey = moment(slot.date, ['YYYY-MM-DD', 'MM/DD/YYYY']).format('YYYY-MM-DD');
            if (!acc[dateKey]) acc[dateKey] = [];
            acc[dateKey].push(slot);
            return acc;
        }, {});
    }, [slots]);

    const isSameSlot = (a, b) =>
        a && b &&
        a.date === b.date &&
        a.starttime === b.starttime &&
        (a.appointmentid ?? a.id) === (b.appointmentid ?? b.id);

    const handleContinue = () => {
        if (!selectedSlot) return;
        navigate('/athena/info', { state: { chosenSlot: selectedSlot } });
    };

    const showEmptyState = !loading && hasFetched && slots.length === 0;
    const showInitialLoading = !loading && !hasFetched && slots.length === 0;

    return (
        <div className="page p-5 max-w-6xl mx-auto">
            <h1 className="text-4xl font-bold mb-4 text-gray-800">
                Choose a time that works for you
            </h1>

            <Loader show={loading} />

            {error && <div className="mb-4 text-red-600">{error}</div>}

            {showEmptyState && (
                <div className="text-center py-12 text-gray-500 text-lg">
                    No available slots found for the next 7 days.
                </div>
            )}
            {showInitialLoading && (
                <div className="text-center py-12 text-gray-500 text-lg">
                    Loading available slots...
                </div>
            )}

            <div className="space-y-4">
                {Object.entries(groupedSlots).map(([date, daySlots]) => (
                    <div key={date} className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
                        <h3 className="text-lg font-semibold text-gray-800 mb-3">
                            {moment(date).format('dddd, MMMM D, YYYY')}
                        </h3>
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                            {daySlots.map((slot, index) => {
                                const isSelected = isSameSlot(selectedSlot, slot);
                                return (
                                    <button
                                        key={`${slot.appointmentid || slot.id || index}-${slot.starttime}`}
                                        type="button"
                                        aria-pressed={isSelected}
                                        onClick={() => setSelectedSlot(slot)}
                                        className={classNames(
                                            'border rounded-lg p-3 text-left transition',
                                            isSelected
                                                ? 'border-blue-500 bg-blue-50 shadow-sm'
                                                : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                                        )}
                                    >
                                        <div className="font-medium text-gray-800">
                                            {moment(slot.starttime, ['HH:mm', 'hh:mm A']).format('hh:mm A')}
                                        </div>
                                        <div className="text-xs text-gray-500">
                                            {slot.duration ? `${slot.duration} min` : 'by time'}
                                        </div>
                                        <div className="text-xs text-gray-400">
                                            {slot.patientappointmenttypename || 'Available'}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>

            {selectedSlot && (
                <div className="mt-6 flex justify-end">
                    <button
                        onClick={handleContinue}
                        className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                        Continue with chosen time
                    </button>
                </div>
            )}
        </div>
    );
};

export default AthenaWelcome;